import { PhotoRecord } from "@/lib/catalog"
import {
	autoSelectNextPhoto,
	currentPhotoAtom,
	displayedPhotosAtom,
	photosAtom,
	selectedPhotosIDsAtom,
} from "./photos-state"
import { saveCurrentAppState } from "../store-history"
import { store } from "@/store/store"
import {
	ClusterInfo,
	clusteredCoverPhotosAtom,
	currentClusterAtom,
	currentClusterIdAtom,
	currentClusterPhotosAtom,
	isShowingClustersAtom,
	photoClustersListAtom,
} from "../importing/culling-state"
import { atom } from "jotai"
import { isRatingBarActiveAtom } from "../ui-views-state"

export const nextPhotoInCluster = (options: { loop: boolean } = { loop: false }) => {
	const currentCluster = store.get(currentClusterAtom)
	if (!currentCluster) {
		return
	}
	const currentPhoto = store.get(currentPhotoAtom)
	const photos = store.get(photosAtom)

	const clusterPhotos = store.get(currentClusterPhotosAtom)
	const originalIndex = currentPhoto
		? clusterPhotos.findIndex((photo) => photo.id === currentPhoto?.id)
		: 0
	if (originalIndex === clusterPhotos.length - 1) {
		//Continue to next cluster
		const clusters = store.get(photoClustersListAtom)
		const currentClusterIndex = clusters.findIndex(
			(cluster) => cluster.clusterLeaderId === currentCluster?.clusterLeaderId,
		)

		if (currentClusterIndex + 1 >= clusters.length && !options.loop) {
			return
		}

		const nextClusterIndex = currentClusterIndex + 1
		const nextCluster =
			nextClusterIndex >= clusters.length ? clusters.at(0) : clusters[nextClusterIndex]
		const firstPhotoIdInNextCluster = nextCluster?.clusterPhotoIds.at(0)
		const photo = firstPhotoIdInNextCluster ? photos[firstPhotoIdInNextCluster] : null

		if (photo) {
			store.set(isRatingBarActiveAtom, false)
			selectPhoto(photo)
		}
		return
	}

	const index = (originalIndex + 1) % clusterPhotos.length
	const photo = clusterPhotos[index]
	if (photo) {
		store.set(isRatingBarActiveAtom, false)
		selectPhoto(photo)
	}
}

export const prevPhotoInCluster = (options: { loop: boolean } = { loop: false }) => {
	const currentCluster = store.get(currentClusterAtom)
	if (!currentCluster) {
		return
	}

	const clusterPhotos = store.get(currentClusterPhotosAtom)
	const currentPhoto = store.get(currentPhotoAtom)
	const photos = store.get(photosAtom)

	const originalIndex = currentPhoto
		? clusterPhotos.findIndex((photo) => photo.id === currentPhoto?.id)
		: 0

	const index = originalIndex - 1

	if (index < 0) {
		//Continue to prev cluster
		const clusters = store.get(photoClustersListAtom)
		const currentClusterIndex = clusters.findIndex(
			(cluster) => cluster.clusterLeaderId === currentCluster?.clusterLeaderId,
		)

		if (currentClusterIndex === 0 && !options.loop) {
			return
		}

		const prevClusterIndex = currentClusterIndex - 1

		const prevCluster = prevClusterIndex < 0 ? clusters.at(-1) : clusters[prevClusterIndex]
		const lastPhotoIdInPrevCluster = prevCluster?.clusterPhotoIds.at(-1)
		const photo = lastPhotoIdInPrevCluster ? photos[lastPhotoIdInPrevCluster] : null
		if (photo) {
			store.set(isRatingBarActiveAtom, false)
			selectPhoto(photo)
		}
		return
	}

	const photo = clusterPhotos[index]
	if (photo) {
		store.set(isRatingBarActiveAtom, false)
		selectPhoto(photo)
	}
}

export const nextCluster = (options: { loop: boolean } = { loop: false }) => {
	const clusters = store.get(photoClustersListAtom)
	const currentClusterId = store.get(currentClusterIdAtom)

	const currentIndex = currentClusterId
		? clusters.findIndex((cluster) => cluster.clusterLeaderId === currentClusterId)
		: 0

	if (currentIndex + 1 >= clusters.length && !options.loop) {
		return
	}

	const index = (currentIndex + 1) % clusters.length
	const nextCluster = clusters[index]
	const photo = nextCluster ? store.get(photosAtom)[nextCluster.coverPhotoId] : null

	if (photo) {
		store.set(isRatingBarActiveAtom, false)
		selectPhoto(photo)
	}
}

export const prevCluster = (options: { loop: boolean } = { loop: false }) => {
	const clusters = store.get(photoClustersListAtom)
	const currentClusterId = store.get(currentClusterIdAtom)

	const currentIndex = currentClusterId
		? clusters.findIndex((cluster) => cluster.clusterLeaderId === currentClusterId)
		: 0

	if (currentIndex === 0 && !options.loop) {
		return
	}

	let index = currentIndex - 1
	if (index < 0 && clusters.length > 0) {
		index = clusters.length - 1
	}
	const prevCluster = clusters[index]
	const photo = prevCluster ? store.get(photosAtom)[prevCluster.coverPhotoId] : null

	if (photo) {
		store.set(isRatingBarActiveAtom, false)
		selectPhoto(photo)
	}
}

export const selectCluster = (cluster: ClusterInfo, fromClick = false) => {
	if (store.get(currentClusterIdAtom) === cluster.clusterLeaderId) {
		return
	}
	const coverPhoto = store.get(photosAtom)[cluster.coverPhotoId]
	if (coverPhoto) {
		store.set(currentPhotoAtom, coverPhoto)
		store.set(selectedPhotosIDsAtom, new Set([coverPhoto.id]))
		store.set(lastSelectionFromClickAtom, fromClick)
	}
}

export const getPhotosAroundCurrentPhoto = () => {
	const displayedPhotos: PhotoRecord[] = store.get(displayedPhotosAtom)
	const currentPhoto = store.get(currentPhotoAtom)

	const originalIndex = currentPhoto
		? displayedPhotos.findIndex((photo) => photo.id === currentPhoto?.id)
		: 0

	const minPhotoIndex = Math.max(originalIndex - 2, 0)
	const maxPhotoIndex = Math.min(originalIndex + 2, displayedPhotos.length)

	return displayedPhotos.slice(minPhotoIndex, maxPhotoIndex)
}

export const nextPhoto = (options: { loop: boolean } = { loop: false }) => {
	if (store.get(isShowingClustersAtom)) {
		nextPhotoInCluster(options)
		return
	}
	const displayedPhotos: PhotoRecord[] = store.get(displayedPhotosAtom)
	const currentPhoto = store.get(currentPhotoAtom)

	const originalIndex = currentPhoto
		? displayedPhotos.findIndex((photo) => photo.id === currentPhoto?.id)
		: 0
	if (originalIndex === displayedPhotos.length - 1 && !options.loop) {
		return
	}

	const index = (originalIndex + 1) % displayedPhotos.length
	const photo = displayedPhotos[index]

	if (photo && index < displayedPhotos.length) {
		store.set(isRatingBarActiveAtom, false)
		selectPhoto(photo)
	}
}

export const prevPhoto = (options: { loop: boolean } = { loop: false }) => {
	if (store.get(isShowingClustersAtom)) {
		prevPhotoInCluster(options)
		return
	}
	const displayedPhotos: PhotoRecord[] = store.get(displayedPhotosAtom)
	const currentPhoto = store.get(currentPhotoAtom)

	const originalIndex = currentPhoto
		? displayedPhotos.findIndex((photo) => photo.id === currentPhoto?.id)
		: 0
	if (originalIndex === 0 && !options.loop) {
		return
	}

	let index = originalIndex - 1
	if (index < 0 && displayedPhotos.length > 0) {
		index = displayedPhotos.length - 1
	}

	const photo = displayedPhotos[index]

	if (photo && index < displayedPhotos.length) {
		store.set(isRatingBarActiveAtom, false)
		selectPhoto(photo)
	}
}

let selectStartIndex: number | null = null

export const selectPhoto = (
	photo: PhotoRecord,
	options?: {
		isShiftPressed?: boolean
		isModPressed?: boolean
		isStackedPhoto?: boolean
		fromClick?: boolean
	},
) => {
	let displayedPhotos: PhotoRecord[] = []
	if (!store.get(isShowingClustersAtom)) {
		displayedPhotos = store.get(displayedPhotosAtom)
	} else if (options?.isStackedPhoto) {
		displayedPhotos = store.get(currentClusterPhotosAtom)
	} else {
		displayedPhotos = store.get(clusteredCoverPhotosAtom)
	}
	const selectedPhotos = store.get(selectedPhotosIDsAtom)
	const currentPhoto = store.get(currentPhotoAtom)

	const newSelectedPhotos = new Set(selectedPhotos)

	const index = displayedPhotos.findIndex((photoRecord) => {
		return photoRecord.id === photo?.id
	})
	const currentPhotoIndex = displayedPhotos.findIndex((photoRecord) => {
		return photoRecord.id === currentPhoto?.id
	})
	const startIndex = selectStartIndex ?? currentPhotoIndex

	if (options?.isShiftPressed) {
		const minIndex: number = Math.min(index, startIndex)
		const maxIndex: number = Math.max(index, startIndex)

		for (let i = minIndex; i <= maxIndex; i++) {
			const photo = displayedPhotos[i]

			if (photo) {
				newSelectedPhotos.add(photo.id)
			}
		}

		// TODO: also deselect photos when holding shift

		selectStartIndex = index
		store.set(selectedPhotosIDsAtom, newSelectedPhotos)
	} else if (options?.isModPressed) {
		if (selectedPhotos?.has(photo.id)) {
			if (selectedPhotos.size === 1) {
				//Do not unselect if its the last photo selected
				return
			}
			newSelectedPhotos.delete(photo.id)

			if (currentPhoto?.id === photo.id) {
				autoSelectNextPhoto([...newSelectedPhotos])
			}
		} else {
			newSelectedPhotos.add(photo.id)
			selectStartIndex = index
		}

		store.set(selectedPhotosIDsAtom, newSelectedPhotos)
	} else {
		store.set(currentPhotoAtom, photo)
		store.set(selectedPhotosIDsAtom, new Set([photo.id]))
		selectStartIndex = index
	}

	// Track if this selection was from a click
	store.set(lastSelectionFromClickAtom, options?.fromClick ?? false)

	saveCurrentAppState("Change selected photos")
}

export const clearSelection = () => {
	store.set(currentPhotoAtom, null)
	store.set(selectedPhotosIDsAtom, new Set())
	saveCurrentAppState(`Clear selection`)
}

export const lastSelectionFromClickAtom = atom(false)
